import { reactive } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'
import type { OperationalPeriod } from '@/types'

interface IncidentSummary {
  latestOp: OperationalPeriod | null
  isCheckedIn: boolean
}

export const useIncidentSummaryStore = defineStore('incidentSummary', () => {
  const summaries = reactive<Map<string, IncidentSummary>>(new Map())

  const opUnsubs = new Map<string, Unsubscribe>()
  const personnelUnsubs = new Map<string, Unsubscribe>()
  const latestOpIds = new Map<string, string>()

  function subscribeToIncident(incidentId: string) {
    if (opUnsubs.has(incidentId)) return

    summaries.set(incidentId, { latestOp: null, isCheckedIn: false })

    const opUnsub = onSnapshot(
      query(
        collection(db, 'incidents', incidentId, 'operational_periods'),
        orderBy('op_number', 'asc'),
      ),
      (snap) => {
        if (snap.empty) {
          summaries.set(incidentId, { latestOp: null, isCheckedIn: false })
          const pUnsub = personnelUnsubs.get(incidentId)
          if (pUnsub) {
            pUnsub()
            personnelUnsubs.delete(incidentId)
          }
          latestOpIds.delete(incidentId)
          return
        }

        const latestDoc = snap.docs[snap.docs.length - 1]!
        const latestOp = { op_id: latestDoc.id, ...latestDoc.data() } as OperationalPeriod
        const newOpId = latestOp.op_id
        const prevOpId = latestOpIds.get(incidentId)

        summaries.set(incidentId, {
          latestOp,
          isCheckedIn: summaries.get(incidentId)?.isCheckedIn ?? false,
        })

        if (newOpId !== prevOpId) {
          latestOpIds.set(incidentId, newOpId)

          const pUnsub = personnelUnsubs.get(incidentId)
          if (pUnsub) {
            pUnsub()
            personnelUnsubs.delete(incidentId)
          }

          const authStore = useAuthStore()
          const uid = authStore.currentUser?.uid
          if (uid) {
            const personnelUnsub = onSnapshot(
              query(
                collection(db, 'incidents', incidentId, 'operational_periods', newOpId, 'personnel'),
                where('user_id', '==', uid),
                where('time_out', '==', null),
              ),
              (pSnap) => {
                summaries.set(incidentId, {
                  latestOp: summaries.get(incidentId)?.latestOp ?? null,
                  isCheckedIn: !pSnap.empty,
                })
              },
            )
            personnelUnsubs.set(incidentId, personnelUnsub)
          }
        }
      },
    )

    opUnsubs.set(incidentId, opUnsub)
  }

  function unsubscribeFromIncident(incidentId: string) {
    opUnsubs.get(incidentId)?.()
    opUnsubs.delete(incidentId)
    personnelUnsubs.get(incidentId)?.()
    personnelUnsubs.delete(incidentId)
    latestOpIds.delete(incidentId)
    summaries.delete(incidentId)
  }

  function subscribeToAll(incidentIds: string[]) {
    const idSet = new Set(incidentIds)
    for (const id of [...opUnsubs.keys()]) {
      if (!idSet.has(id)) unsubscribeFromIncident(id)
    }
    for (const id of incidentIds) {
      subscribeToIncident(id)
    }
  }

  function teardown() {
    for (const id of [...opUnsubs.keys()]) {
      unsubscribeFromIncident(id)
    }
  }

  return { summaries, subscribeToAll, teardown }
})
